import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { aiMarkState } from '../../services/ai/aiAuthorship';
import { readDocumentMeta, saveDocumentTags, normalizeTags } from '../../extensions/bundled/documents/documentsMeta';
import { useDocumentsUiStore, bumpDocumentsTree } from '../../extensions/bundled/documents/documentsUiStore';
import { countWords } from './docMetaFormat';

/** The word count waits for typing to pause this long (card 2.1). */
export const WORD_COUNT_DEBOUNCE_MS = 500;
const TICK_MS = 60_000;

/**
 * The reading-view facts of an open document: tags, creation, last edit and
 * word count (cards 2.1 + 2.3).
 *
 * - Record facts (documentsMeta.readDocumentMeta) reload on the documents
 *   tree nonce, so a tag added on another device or by git shows up.
 * - Words come from the live body through `subscribeBody` (useDocumentBody's
 *   listener set — no re-render per keystroke), debounced 500 ms.
 * - A local edit counts as "edited now" straight away; the record's
 *   `updated_at` catches up once the save lands.
 * - `now` ticks once a minute so "Edited 3 min ago" does not freeze.
 */
// A record without a real timestamp (0, a placeholder like 1 ms, anything
// before 2000) reads as unknown: the toolbar and title block then leave the
// date out instead of showing "1 Jan 1970" (owner screenshot, 14.10.2026).
const YEAR_2000 = Date.UTC(2000, 0, 1);
export const realTime = (t) => {
  const ms = typeof t === 'number' ? t : Date.parse(t);
  return Number.isFinite(ms) && ms >= YEAR_2000 ? t : null;
};

export function useDocumentMeta({ documentId, scope, subscribeBody, ready }) {
  const treeVersion = useDocumentsUiStore((s) => s.treeVersion);
  const [meta, setMeta] = useState({ tags: [], createdAt: null, createdBy: null, updatedAt: null });
  const [words, setWords] = useState(null);
  // The body as last seen (debounced like the word count), so the AI chip can
  // tell whether a person has rewritten the AI's text since (aiAuthorship.js).
  const [bodyHtml, setBodyHtml] = useState(undefined);
  const [localEditAt, setLocalEditAt] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    readDocumentMeta(documentId, scopeRef.current)
      .then((m) => {
        if (!cancelled && m) setMeta({ ...m, createdAt: realTime(m.createdAt), updatedAt: realTime(m.updatedAt) });
      })
      .catch(() => {
        /* the view shows less; the document itself is unaffected */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, ready, treeVersion, scope.type, scope.teamId]);

  useEffect(() => {
    if (!subscribeBody) return undefined;
    let timer = null;
    let edited = false;
    const off = subscribeBody((html, { local } = {}) => {
      clearTimeout(timer);
      if (local) edited = true;
      // A load or remote apply counts at once; typing waits for a pause.
      timer = setTimeout(() => {
        setWords(countWords(html));
        setBodyHtml(html);
        if (edited) setLocalEditAt(Date.now());
        edited = false;
      }, local ? WORD_COUNT_DEBOUNCE_MS : 0);
    });
    return () => {
      clearTimeout(timer);
      off();
    };
  }, [subscribeBody, documentId]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(id);
  }, []);

  const setTags = useCallback(
    async (next) => {
      const tags = normalizeTags(next);
      setMeta((m) => ({ ...m, tags }));
      await saveDocumentTags(documentId, tags, scopeRef.current);
      bumpDocumentsTree();
    },
    [documentId]
  );

  const editedAt = Math.max(meta.updatedAt || 0, localEditAt || 0) || null;
  const aiMark = useMemo(() => aiMarkState(meta.ai, bodyHtml), [meta.ai, bodyHtml]);
  return { ...meta, editedAt, words, aiMark, now: Math.max(now, localEditAt || 0), setTags };
}
